import { useCallback, useState } from "react";
import ContactUsModal from "@components/molecules/ContactUs";
import JBTdropdown from "@components/molecules/JBTdropdown";
import Popup from "@components/molecules/PopUp";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { messageType } from "@components/molecules/InputPopUp/types";
import { useTranslation } from "react-i18next";
import { useSignOut } from "@apis/user";
import useLogout from "@components/common/hooks/useLogout";
import PriceModeModal from "@components/molecules/PriceModeModal/PriceModeModal";
import TermsAndConditionsModal from "@components/molecules/TermsAndConditionModal/TermsAndConditionsModal";
import DeleteAccountModal from "@components/molecules/DeleteAccountModal/DeleteAccountModal";
import DefaultSearchGroupModal from "@components/molecules/DefaultSearchGroupModal/DefaultSearchGroupModal";
import { useOneSignalSubscriberId } from "../../../hooks/useSubscriberId";
import { Modal, Platform } from "react-native";
import Loader from "@components/atoms/Loader";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useDispatch, useSelector } from "react-redux";
import { setIsSignoutUrlRedirecting } from "@redux/slices/appStateSlice";
import CoachOverlayModal from "@components/molecules/CoachOverlayModal";
import PagesScreenModal from "@components/molecules/PagesScreenModal/PagesScreenModal";
import styled from "styled-components/native";

const useMoreScreenModals = () => {
  const [successResponseData, setSuccessResponseData] = useState<{
    title: string;
    message: string;
  }>({ title: "", message: "" });
  const [modalType, setModalType] = useState<{
    value:
      | "contact-us"
      | "price-mode"
      | "coach-overlay"
      | "terms-and-conditions"
      | "default-search-group"
      | "delete-account"
      | "jbt"
      | "sign-out-popup"
      | "success-popup"
      | "pages"
      | "loader-modal"
      | null;
    params: any;
  }>({ value: null, params: {} });
  const { t } = useTranslation();
  const logout = useLogout();
  const dispatch = useDispatch();
  const { mutateAsync: mutateSignOutAsync, isLoading: isSignOutLoading } =
    useSignOut();
  const { handleDeleteOneSignalToken } = useOneSignalSubscriberId();
  const { organization, isJbtDisabled } = useOrgSettingsSlice();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const closePopup = async (shouldLogout: boolean) => {
    if (shouldLogout) {
      if (
        Platform.OS === "web" &&
        isCustomApp &&
        organization.redirect_urls.sign_out_url?.length > 0
      ) {
        dispatch(setIsSignoutUrlRedirecting(true));
        setModalType("loader-modal");
      }
      await handleDeleteOneSignalToken();
      await mutateSignOutAsync();
      if (
        !(
          Platform.OS === "web" &&
          isCustomApp &&
          organization.redirect_urls.sign_out_url?.length > 0
        )
      )
        setModalType({ value: null, params: {} });
      await logout();
      if (
        Platform.OS === "web" &&
        isCustomApp &&
        organization.redirect_urls.sign_out_url?.length > 0
      ) {
        document.location.href = organization.redirect_urls.sign_out_url;
      }
    } else {
      setModalType({ value: null, params: {} });
    }
  };

  const handleCloseSuccessPopup = () =>
    setModalType({ value: null, params: {} });
  const handleOpenSuccessPopup = () =>
    setModalType({ value: "success-popup", params: {} });

  const renderModals = useCallback(() => {
    return (
      <>
        <ContactUsModal
          isOpen={modalType?.value === "contact-us"}
          onClose={() => setModalType({ value: null, params: {} })}
          screenName={"More & Settings"}
          onResponseDataChange={setSuccessResponseData}
          showSuccess={handleOpenSuccessPopup}
        />
        <PriceModeModal
          isOpen={modalType?.value === "price-mode"}
          onClose={() => handleCloseSuccessPopup()}
        />
        <CoachOverlayModal
          isOpen={modalType?.value === "coach-overlay"}
          onClose={() => handleCloseSuccessPopup()}
        />
        <TermsAndConditionsModal
          isOpen={modalType?.value === "terms-and-conditions"}
          onClose={() => handleCloseSuccessPopup()}
        />
        <DeleteAccountModal
          isOpen={modalType?.value === "delete-account"}
          onClose={() => handleCloseSuccessPopup()}
        />
        <DefaultSearchGroupModal
          isOpen={modalType?.value === "default-search-group"}
          onClose={() => handleCloseSuccessPopup()}
        />
        {!isJbtDisabled && (
          <JBTdropdown
            isOpen={modalType?.value === "jbt"}
            onClose={() => handleCloseSuccessPopup()}
            HtmlSource={{
              html: t("jbt.screenText.footer.text"),
            }}
            styleKey={"Minor-R"}
          />
        )}
        <PagesScreenModal
          isOpen={modalType?.value === "pages"}
          onClose={() => handleCloseSuccessPopup()}
          params={modalType?.params}
        />
        {modalType?.value === "sign-out-popup" && (
          <Popup
            title={t("signOut.title.text")}
            buttons={[
              {
                type: ButtonType.primary,
                title: t("signOut.button.primary.text"),
                onPressAction: () => closePopup(true),
                loading: isSignOutLoading,
                disabled: isSignOutLoading,
              },
              {
                type: ButtonType.secondary,
                title: t("signOut.button.secondary.text"),
                onPressAction: () => closePopup(false),
              },
            ]}
            HtmlSource={{ html: t("signOut.discription.text") }}
            styleKey="Body-R"
            type={messageType.Error}
          />
        )}
        {modalType?.value === "success-popup" && (
          <Popup
            type={messageType.Success}
            title={successResponseData?.title}
            buttons={[
              {
                type: ButtonType.primary,
                title: t("contactUs.successPopUp.button.title.text"),
                onPressAction: handleCloseSuccessPopup,
                category: ButtonCategory.default,
              },
            ]}
            HtmlSource={{ html: `${successResponseData?.message}` }}
            styleKey="Body-R"
          />
        )}
        {modalType === "loader-modal" && (
          <Modal
            animationType="none"
            transparent={true}
            visible={true}
            supportedOrientations={["landscape", "portrait"]}
          >
            <CenteredView>
              <Loader />
            </CenteredView>
          </Modal>
        )}
      </>
    );
  }, [modalType, isSignOutLoading]);

  const showModal = useCallback((value: string, params?: any) => {
    setModalType({
      value,
      params,
    });
  }, []);

  return { renderModals, showModal };
};

const CenteredView = styled.View<any>`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme["core-01"]};
  height: 100%;
`;

export default useMoreScreenModals;
